export type CapitalPartnerSectionId =
  | 'hero'
  | 'whyNow'
  | 'marketProblem'
  | 'uniquePosition'
  | 'networkDefinition'
  | 'howItWorks'
  | 'investorBenefits'
  | 'businesses'
  | 'whoShouldJoin'
  | 'application';

export type CapitalPartnerApplicationFormState = {
  fullName: string;
  title: string;
  firmName: string;
  email: string;
  phone: string;
  websiteOrLinkedIn: string;
  investorType: string;
  typicalCheckSize: string;
  preferredStages: string;
  sectorFocus: string;
  geographicPreferences: string;
  preferredDealTypes: string;
  whatMattersMost: string;
  valueBeyondCapital: string;
  relevantExperience: string;
  avoids: string;
  comments: string;
};

const CONTACT_EMAIL = 'Cybard.design@gmail.com';
export const CAPITAL_PARTNER_NETWORK_PHONE_HREF = 'tel:+15202303957';
export const CAPITAL_PARTNER_NETWORK_EMAIL_HREF =
  'mailto:Cybard.design@gmail.com?subject=Capital%20Partner%20Network';

export const CAPITAL_PARTNER_NETWORK_META = {
  title: 'Capital Partner Network',
  description:
    'Curated access to growth-ready businesses inside the CyBard ecosystem, backed by operating insight rather than a pitch deck alone.',
} as const;

export const CAPITAL_PARTNER_SECTION_ORDER: readonly CapitalPartnerSectionId[] = [
  'hero',
  'whyNow',
  'marketProblem',
  'uniquePosition',
  'networkDefinition',
  'howItWorks',
  'investorBenefits',
  'businesses',
  'whoShouldJoin',
  'application',
] as const;

export const CAPITAL_PARTNER_INVESTOR_TYPE_OPTIONS = [
  'Fund',
  'Family office',
  'Strategic operator',
  'Angel / individual investor',
  'Private investment group',
  'Other',
] as const;

export const EMPTY_CAPITAL_PARTNER_APPLICATION_FORM: CapitalPartnerApplicationFormState = {
  fullName: '',
  title: '',
  firmName: '',
  email: '',
  phone: '',
  websiteOrLinkedIn: '',
  investorType: '',
  typicalCheckSize: '',
  preferredStages: '',
  sectorFocus: '',
  geographicPreferences: '',
  preferredDealTypes: '',
  whatMattersMost: '',
  valueBeyondCapital: '',
  relevantExperience: '',
  avoids: '',
  comments: '',
};

export const CAPITAL_PARTNER_NETWORK_CONTENT = {
  hero: {
    eyebrow: 'Capital Partner Network',
    title: 'Access growth-ready businesses before they become obvious.',
    subheadline:
      'Our Capital Partner Network gives investors curated access to select businesses inside our ecosystem, companies we already help strengthen through assets & design, managed marketing, and platform development. When a business shows real potential for national or global growth, we create aligned introductions backed by operating insight, not just a pitch deck.',
    primaryCta: {
      label: 'Apply to Join the Network',
      href: '#application-form',
    },
  },
  whyNow: {
    eyebrow: 'Why now',
    title: 'A new growth cycle is taking shape.',
    body:
      'AI is changing what small and mid-sized businesses can accomplish with leaner teams. At the same time, a massive generational wealth transfer is likely to reshape where capital flows over the next decade. The result is a market where investors need better signal, better timing, and better access to businesses that can scale efficiently.',
    proofStrip: [
      'AI is increasing output per worker.',
      'Revenue per employee has risen meaningfully across major tech firms.',
      'The report cites roughly $77T in baby boomer household wealth in 2022 and an estimated ~$84.4T transfer through 2045.',
    ],
  },
  marketProblem: {
    eyebrow: 'The market problem',
    title: 'Capital has more options than ever and less clarity.',
    paragraphs: [
      'Investors are surrounded by deal flow, but very little of it comes with real operating visibility. Founders often reach capital markets when the story is polished but the underlying execution is still uncertain.',
      'At the same time, many businesses with real potential struggle to find investors who are actually aligned with their stage, strategy, and scale path. The inefficiency is not lack of opportunity. It is lack of context around which opportunities are actually worth conviction.',
    ],
  },
  uniquePosition: {
    eyebrow: 'Why CyBard',
    title: 'We know these businesses from the inside.',
    paragraphs: [
      'We are not meeting companies at the surface level. We are already working alongside many of them through assets & design, managed marketing, and platform development. That gives us a deeper view into execution quality, market traction, brand maturity, customer signal, and growth readiness.',
      'When we advocate for a business, we are doing it from proximity and experience, not from a deck alone. That is the trust layer. It is why the network is curated instead of transactional.',
    ],
  },
  networkDefinition: {
    eyebrow: 'What the network is',
    title: 'A curated bridge between capital and growth-ready businesses.',
    paragraphs: [
      'The Capital Partner Network is a relationship-driven ecosystem built to connect aligned investors with vetted businesses that show meaningful expansion potential.',
      'This is not a public marketplace and not a mass deal list. We make selective introductions when timing, fit, and growth potential are all there.',
    ],
    note:
      'The model is curated access to select businesses in our ecosystem, not direct access to a client list.',
  },
  howItWorks: {
    eyebrow: 'How it works',
    title: 'How we turn visibility into aligned introductions.',
    steps: [
      {
        step: '01',
        title: 'We work closely with businesses.',
        body:
          'Because we are involved in key parts of growth execution, we can see which companies are building real momentum.',
      },
      {
        step: '02',
        title: 'We identify capital readiness.',
        body:
          'Not every business is ready for investor introductions. We look for businesses that have the leadership, market potential, and infrastructure to grow beyond their current scale.',
      },
      {
        step: '03',
        title: 'We match for fit.',
        body:
          'We consider stage, sector, thesis, check size, strategic value, geography, and long-term alignment before making introductions.',
      },
      {
        step: '04',
        title: 'We stay close after the introduction.',
        body:
          'Because we already understand the business, we can continue supporting the growth story after the connection is made.',
      },
    ],
  },
  investorBenefits: {
    eyebrow: 'Investor value',
    title: 'Better access. Better context. Better alignment.',
    body:
      'Investors in the network gain access to opportunities that are filtered through real operating insight. They get earlier visibility into companies before they become widely marketed, more context around execution strength, and introductions shaped by fit rather than volume.',
    benefits: [
      {
        title: 'Curated opportunity flow',
        body: 'Selective, not noisy.',
      },
      {
        title: 'Operator-level insight',
        body: 'More than a deck and financial summary.',
      },
      {
        title: 'Aligned introductions',
        body: 'Based on thesis and growth fit.',
      },
      {
        title: 'Ongoing visibility',
        body: 'Continued relationship beyond the first call.',
      },
    ],
  },
  businesses: {
    eyebrow: 'What we advocate for',
    title: 'We bring forward businesses with real expansion paths.',
    body:
      'Our ecosystem includes small, medium, and larger businesses, but the filter is not company size alone. The filter is potential. We advocate for businesses that show evidence of traction, operational readiness, and a credible path toward regional, national, or global growth.',
    qualificationIntro:
      'We look for businesses with a differentiated offer, clear market demand, capable leadership, scalable systems, and meaningful upside if paired with the right capital and strategic support.',
    qualifications: [
      'A differentiated offer that can hold attention in-market',
      'Clear market demand and visible customer pull',
      'Capable leadership able to convert strategy into execution',
      'Scalable systems that can support growth beyond the current stage',
      'Meaningful upside when paired with aligned capital and strategic support',
    ],
  },
  whoShouldJoin: {
    eyebrow: 'Who should join',
    title: 'Built for investors who value signal over noise.',
    body:
      'This network is designed for investors, family offices, funds, strategic operators, and other capital partners looking for thoughtful access to growth-ready businesses. The best-fit partners are those with a clear thesis, realistic time horizon, and a desire to back businesses where execution strength is visible, not hypothetical.',
    profiles: [
      'Investors',
      'Family offices',
      'Funds',
      'Strategic operators',
      'Capital partners with a clear thesis',
    ],
  },
  application: {
    eyebrow: 'Application',
    title: 'Apply to Join the Capital Partner Network',
    intro:
      'Tell us how you invest, what you look for, and where you create the most value. We review every application for fit and make introductions selectively based on readiness, alignment, and opportunity.',
    submitLabel: 'Submit Application',
    microcopy:
      'Applications are reviewed for fit. Network participation and introductions are selective.',
    fields: {
      fullName: 'Full name',
      title: 'Title',
      firmName: 'Firm / family office / company name',
      email: 'Email',
      phone: 'Phone',
      websiteOrLinkedIn: 'Website or LinkedIn',
      investorType: 'Investor type',
      typicalCheckSize: 'Typical check size',
      preferredStages: 'Preferred stages',
      sectorFocus: 'Sector or thematic focus',
      geographicPreferences: 'Geographic preferences',
      preferredDealTypes: 'Preferred deal types or structures',
      whatMattersMost: 'What do you look for most in a business?',
      valueBeyondCapital: 'How do you add value beyond capital?',
      relevantExperience: 'Relevant investments or experience',
      avoids: 'Anything you do not invest in',
      comments: 'Open comments',
    },
  },
} as const;

export function buildCapitalPartnerApplicationMailto(
  form: CapitalPartnerApplicationFormState,
) {
  const subject = `Capital Partner Network Application - ${form.fullName.trim() || 'New applicant'}`;
  const body = [
    'Capital Partner Network Application',
    '',
    `Full name: ${form.fullName.trim() || '--'}`,
    `Title: ${form.title.trim() || '--'}`,
    `Firm / family office / company name: ${form.firmName.trim() || '--'}`,
    `Email: ${form.email.trim() || '--'}`,
    `Phone: ${form.phone.trim() || '--'}`,
    `Website or LinkedIn: ${form.websiteOrLinkedIn.trim() || '--'}`,
    `Investor type: ${form.investorType.trim() || '--'}`,
    `Typical check size: ${form.typicalCheckSize.trim() || '--'}`,
    `Preferred stages: ${form.preferredStages.trim() || '--'}`,
    `Sector or thematic focus: ${form.sectorFocus.trim() || '--'}`,
    `Geographic preferences: ${form.geographicPreferences.trim() || '--'}`,
    `Preferred deal types or structures: ${form.preferredDealTypes.trim() || '--'}`,
    '',
    'What do you look for most in a business?',
    form.whatMattersMost.trim() || '--',
    '',
    'How do you add value beyond capital?',
    form.valueBeyondCapital.trim() || '--',
    '',
    'Relevant investments or experience',
    form.relevantExperience.trim() || '--',
    '',
    'Anything you do not invest in',
    form.avoids.trim() || '--',
    '',
    'Open comments',
    form.comments.trim() || '--',
  ].join('\n');

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
